import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { FaWater, FaCog, FaCheckCircle, FaTools, FaChartLine, FaShieldAlt, FaRecycle, FaIndustry } from 'react-icons/fa';

const WaterSoftener = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={8}>
              <h1 className="display-4 fw-bold mb-4">Water Softener Systems</h1>
              <p className="lead mb-4">
                Advanced ion exchange water softening solutions to eliminate hard water problems, 
                protect equipment, and improve operational efficiency across industrial applications.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Overview */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">What is Water Softening?</h2>
              <p className="lead">
                Water softening is the process of removing calcium and magnesium ions from hard water 
                through ion exchange, preventing scale formation and protecting industrial equipment.
              </p>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col lg={6}>
              <h4 className="mb-3">Hard Water Problems:</h4>
              <ul>
                <li>Scale formation in pipes and equipment</li>
                <li>Reduced heat transfer efficiency</li>
                <li>Increased energy consumption</li>
                <li>Premature equipment failure</li>
                <li>Higher maintenance costs</li>
              </ul>
            </Col>
            <Col lg={6}>
              <h4 className="mb-3">Benefits of Soft Water:</h4>
              <ul>
                <li>Extended equipment lifespan</li>
                <li>Improved energy efficiency</li>
                <li>Reduced maintenance requirements</li>
                <li>Better product quality</li>
                <li>Lower operating costs</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* System Types */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Water Softener System Types</h2>
              <p className="lead">
                We offer various softening systems tailored to different industrial requirements and water conditions.
              </p>
            </Col>
          </Row>
          
          <Row>
            <Col md={6} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body className="text-center">
                  <div className="feature-icon mb-3"><FaIndustry /></div>
                  <Card.Title>Industrial Twin Tank Systems</Card.Title>
                  <Card.Text>
                    Continuous soft water supply with automatic switching between tanks for uninterrupted operations.
                  </Card.Text>
                  <Badge bg="primary">High Capacity</Badge>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body className="text-center">
                  <div className="feature-icon mb-3"><FaCog /></div>
                  <Card.Title>Single Tank Systems</Card.Title>
                  <Card.Text>
                    Cost-effective solution for smaller operations with periodic regeneration cycles.
                  </Card.Text>
                  <Badge bg="success">Economical</Badge>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body className="text-center">
                  <div className="feature-icon mb-3"><FaRecycle /></div>
                  <Card.Title>Water Softener with RO Integration</Card.Title>
                  <Card.Text>
                    Combined system providing both soft water and purified water for critical applications.
                  </Card.Text>
                  <Badge bg="info">Integrated Solution</Badge>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Key Features */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Key Features & Capabilities</h2>
              <p className="lead">Advanced features for optimal performance</p>
            </Col>
          </Row>
          <Row>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaCog /></div>
                <h5>Automatic Regeneration</h5>
                <p className="small">Self-regenerating systems with programmable cycles for minimal downtime</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaChartLine /></div>
                <h5>Smart Controls</h5>
                <p className="small">Digital monitoring and control systems with real-time performance data</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaShieldAlt /></div>
                <h5>Durable Construction</h5>
                <p className="small">High-quality materials resistant to corrosion and chemical degradation</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaWater /></div>
                <h5>High Flow Rates</h5>
                <p className="small">Systems designed for various flow capacities from small to large industrial needs</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Applications */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Industrial Applications</h2>
              <p className="lead">Water softening solutions for various industries</p>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaTools className="me-2" />Manufacturing</h4>
              <ul>
                <li>Boiler feed water treatment</li>
                <li>Cooling tower make-up water</li>
                <li>Process water preparation</li>
                <li>Equipment cleaning systems</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaIndustry className="me-2" />Hospitality & Healthcare</h4>
              <ul>
                <li>Laundry operations</li>
                <li>Kitchen equipment protection</li>
                <li>Steam generation systems</li>
                <li>Sanitation and cleaning</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaWater className="me-2" />Food & Beverage</h4>
              <ul>
                <li>Ingredient water preparation</li>
                <li>Bottle washing systems</li>
                <li>CIP (Clean-in-Place) systems</li>
                <li>Steam boiler protection</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaCog className="me-2" />Power Generation</h4>
              <ul>
                <li>Boiler feed water treatment</li>
                <li>Cooling system protection</li>
                <li>Turbine protection</li>
                <li>Heat exchanger efficiency</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Process Overview */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Ion Exchange Process</h2>
              <p className="lead">How water softening works</p>
            </Col>
          </Row>
          <Row>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3">1</div>
                <h5>Hard Water Inlet</h5>
                <p className="small">Raw hard water containing calcium and magnesium ions enters the system</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3">2</div>
                <h5>Ion Exchange</h5>
                <p className="small">Hard water passes through resin beads that exchange sodium ions for hardness ions</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3">3</div>
                <h5>Soft Water Outlet</h5>
                <p className="small">Treated soft water exits the system free of calcium and magnesium ions</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3">4</div>
                <h5>Regeneration</h5>
                <p className="small">Resin is regenerated using brine solution to restore capacity</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Technical Specifications */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Technical Specifications</h2>
              <p className="lead">System capabilities and performance parameters</p>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6}>
              <h4 className="mb-3">Performance Parameters:</h4>
              <ul>
                <li>Hardness removal: Up to 99% efficiency</li>
                <li>Flow rates: 100 to 50,000 LPH</li>
                <li>Operating pressure: 2-6 bar</li>
                <li>Temperature range: 5-45°C</li>
                <li>Regeneration time: 60-120 minutes</li>
              </ul>
            </Col>
            <Col lg={6}>
              <h4 className="mb-3">System Components:</h4>
              <ul>
                <li>High-grade ion exchange resin</li>
                <li>FRP/MS pressure vessels</li>
                <li>Automatic control valves</li>
                <li>Brine preparation system</li>
                <li>Digital monitoring systems</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-primary text-white">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold mb-4">Need a Water Softening Solution?</h2>
              <p className="lead mb-4">
                Contact us for a detailed water analysis and customized softening system design.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Button variant="warning" size="lg" className="cta-button">
                  Get Water Analysis
                </Button>
                <Button variant="outline-light" size="lg">
                  Request Quote
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default WaterSoftener;
